import { storageService } from "@/lib/storage";
import { shiftsApi } from "@/features/shifts/api";
import {
  SHIFT_API_SETTINGS_QUERY_KEY,
  type EmployeeShiftApiSettings,
} from "@/features/shifts/employeeShiftApiSettings";
import { queryClient } from "@/shared/queryClient";
import { ApiError } from "@/shared/apiClient";
import type { Shift } from "@/features/shifts/types";
import { OFFLINE_SHIFT_PLACEHOLDER_ID, buildProvisionalStartShift } from "./offlineShiftPlaceholders";
import { logger } from "@/shared/logger";

const OUTBOX_STORAGE_KEY = "shifts.action_outbox.v1";

export type ShiftStartOutboxPayload = {
  notes?: string;
  manualArrivalTime?: string;
  coords?: { latitude: number; longitude: number } | null;
  timezone: string | null;
};

export type ShiftEndOutboxPayload = {
  notes?: string;
  manualEndTime?: string;
  endOfShiftChecklist?: Array<{ item: string; completed: boolean }>;
  coords?: { latitude: number; longitude: number } | null;
};

export type ShiftActionOutboxItem =
  | {
      id: string;
      kind: "start";
      clientRecordedAt: string;
      payload: ShiftStartOutboxPayload;
    }
  | {
      id: string;
      kind: "end";
      clientRecordedAt: string;
      payload: ShiftEndOutboxPayload;
    };

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export async function loadShiftActionOutbox(): Promise<ShiftActionOutboxItem[]> {
  const raw = await storageService.get<ShiftActionOutboxItem[]>(OUTBOX_STORAGE_KEY);
  if (!Array.isArray(raw)) return [];
  return raw;
}

async function saveShiftActionOutbox(items: ShiftActionOutboxItem[]): Promise<void> {
  await storageService.set(OUTBOX_STORAGE_KEY, items);
}

export async function clearShiftActionOutbox(): Promise<void> {
  await storageService.remove(OUTBOX_STORAGE_KEY);
}

export async function enqueueShiftStart(
  payload: ShiftStartOutboxPayload
): Promise<ShiftActionOutboxItem & { kind: "start" }> {
  const row: ShiftActionOutboxItem & { kind: "start" } = {
    id: newId(),
    kind: "start",
    clientRecordedAt: new Date().toISOString(),
    payload,
  };
  const existing = await loadShiftActionOutbox();
  existing.push(row);
  await saveShiftActionOutbox(existing);
  logger.info("shifts.outbox.enqueuedStart", {
    id: row.id,
    queueSize: existing.length,
    hasManualArrival: !!row.payload.manualArrivalTime,
    hasCoords: !!row.payload.coords,
  });
  return row;
}

export async function enqueueShiftEnd(
  payload: ShiftEndOutboxPayload
): Promise<ShiftActionOutboxItem & { kind: "end" }> {
  const row: ShiftActionOutboxItem & { kind: "end" } = {
    id: newId(),
    kind: "end",
    clientRecordedAt: new Date().toISOString(),
    payload,
  };
  const existing = await loadShiftActionOutbox();
  existing.push(row);
  await saveShiftActionOutbox(existing);
  logger.info("shifts.outbox.enqueuedEnd", {
    id: row.id,
    queueSize: existing.length,
    hasManualEnd: !!row.payload.manualEndTime,
    checklistCount: row.payload.endOfShiftChecklist?.length ?? 0,
  });
  return row;
}

async function removeShiftActionOutboxItem(id: string): Promise<void> {
  const existing = await loadShiftActionOutbox();
  await saveShiftActionOutbox(existing.filter((x) => x.id !== id));
}

/**
 * Replay queued start/end ops locally to derive what the open shift should look like before the server catches up.
 */
export function deriveOptimisticOpenShiftFromQueue(
  items: ShiftActionOutboxItem[],
  employeeId: number
): Shift | null {
  let open: Shift | null = null;
  for (const item of items) {
    if (item.kind === "start") {
      open = buildProvisionalStartShift(
        {
          notes: item.payload.notes,
          manualArrivalTime: item.payload.manualArrivalTime,
          coords: item.payload.coords ?? null,
        },
        employeeId,
        item.clientRecordedAt
      );
    } else {
      open = null;
    }
  }
  return open;
}

let flushInFlight = false;

export async function flushShiftActionOutbox(): Promise<void> {
  if (flushInFlight) return;
  flushInFlight = true;
  logger.info("shifts.outbox.flush.start");
  try {
    while (true) {
      const items = await loadShiftActionOutbox();
      if (items.length === 0) break;
      const head = items[0];
      logger.info("shifts.outbox.flush.attempt", {
        id: head.id,
        kind: head.kind,
      });
      try {
        if (head.kind === "start") {
          const p = head.payload;
          const { shift, settings } = await shiftsApi.startShift(
            p.notes,
            p.manualArrivalTime ?? undefined,
            p.coords ?? null
          );
          if (settings) {
            queryClient.setQueryData<EmployeeShiftApiSettings | undefined>(
              SHIFT_API_SETTINGS_QUERY_KEY,
              settings
            );
          }
          await removeShiftActionOutboxItem(head.id);
          logger.info("shifts.outbox.flush.startSuccess", { id: head.id });
          queryClient.setQueryData(["shifts", "open"], shift);
          queryClient.invalidateQueries({ queryKey: ["shifts", "list"] });
          queryClient.invalidateQueries({ queryKey: ["checklist"] });
          queryClient.invalidateQueries({ queryKey: ["shifts", "outbox"] });
          queryClient.invalidateQueries({ queryKey: ["patrol", "status"] });
          queryClient.invalidateQueries({ queryKey: ["patrol", "config"] });
        } else {
          const p = head.payload;
          const { settings } = await shiftsApi.endShift(
            p.notes,
            p.manualEndTime ?? undefined,
            p.endOfShiftChecklist,
            p.coords ?? null
          );
          if (settings) {
            queryClient.setQueryData<EmployeeShiftApiSettings | undefined>(
              SHIFT_API_SETTINGS_QUERY_KEY,
              settings
            );
          }
          await removeShiftActionOutboxItem(head.id);
          logger.info("shifts.outbox.flush.endSuccess", { id: head.id });
          queryClient.setQueryData(["shifts", "open"], null);
          queryClient.invalidateQueries({ queryKey: ["shifts", "open"] });
          queryClient.invalidateQueries({ queryKey: ["shifts", "list"] });
          queryClient.invalidateQueries({ queryKey: ["checklist"] });
          queryClient.invalidateQueries({ queryKey: ["shifts", "outbox"] });
          queryClient.invalidateQueries({ queryKey: ["patrol", "status"] });
          queryClient.invalidateQueries({ queryKey: ["patrol", "config"] });
        }
      } catch (err) {
        logger.warn("shifts.outbox.flush.error", {
          id: head.id,
          kind: head.kind,
          error: err,
        });
        if (err instanceof ApiError) {
          if (err.status === 401) {
            logger.warn("shifts.outbox.flush.stopOn401", {
              id: head.id,
              kind: head.kind,
            });
            break;
          }
          if (err.status === 404 || err.status === 409) {
            logger.warn("shifts.outbox.flush.dropNonRetryable", {
              id: head.id,
              kind: head.kind,
              status: err.status,
            });
            await removeShiftActionOutboxItem(head.id);
            queryClient.invalidateQueries({ queryKey: ["shifts", "outbox"] });
            queryClient.invalidateQueries({ queryKey: ["shifts", "open"] });
            queryClient.invalidateQueries({ queryKey: ["shifts", "list"] });
            continue;
          }
        }
        break;
      }
    }
  } finally {
    flushInFlight = false;
    logger.info("shifts.outbox.flush.end");
  }
}

export async function hydrateShiftOutboxIntoQueryClient(): Promise<void> {
  const user = queryClient.getQueryData<{ id?: number } | null>(["auth", "user"]);
  const employeeId = typeof user?.id === "number" ? user.id : null;
  if (employeeId == null) return;

  const items = await loadShiftActionOutbox();
  if (items.length === 0) return;
  logger.info("shifts.outbox.hydrate.start", {
    employeeId,
    queueSize: items.length,
  });

  const optimistic = deriveOptimisticOpenShiftFromQueue(items, employeeId);
  const current = queryClient.getQueryData<Shift | null>(["shifts", "open"]);

  if (optimistic == null) {
    if (current?.offlinePending === true && current.id === OFFLINE_SHIFT_PLACEHOLDER_ID) {
      queryClient.setQueryData(["shifts", "open"], null);
    }
    return;
  }

  if (
    current == null ||
    current.offlinePending === true ||
    current.id === OFFLINE_SHIFT_PLACEHOLDER_ID
  ) {
    queryClient.setQueryData(["shifts", "open"], optimistic);
    logger.info("shifts.outbox.hydrate.setOptimisticOpenShift", {
      employeeId,
      shiftId: optimistic.id,
    });
  }
}
